import { useEffect } from 'react';
import { Cable, Wifi, WifiOff, ArrowRight, X } from 'lucide-react';
import clsx from 'clsx';
import { useTransport } from '@/store/transport';
import { useDeviceDisplayName } from '@/lib/displayName';

/**
 * Bottom-right floating region for `transport-change` failover
 * notifications — separate from {@link AutoswitchToasts} so the two
 * concerns (provisioning result vs runtime failover) stack
 * independently and can be styled differently.
 *
 * Toasts surfaced here cover:
 *   - USB → Wi-Fi flip ("已切换到 Wi-Fi 备用线")
 *   - any → offline   ("设备 离线")
 *   - offline → any   ("设备 恢复联机 (USB|Wi-Fi)")
 */
const DISMISS_MS = 6_000;

function describeKind(k: string | undefined): string {
  if (!k) return '离线';
  if (k === 'usb') return 'USB';
  if (k === 'wireless') return 'Wi-Fi（无线调试）';
  if (k === 'tcpip') return 'TCP-IP 镜像备线';
  return k;
}

export function TransportToasts(): JSX.Element | null {
  const toasts = useTransport((s) => s.recentFailovers);
  const dismiss = useTransport((s) => s.dismissFailover);

  useEffect(() => {
    const timers: number[] = [];
    for (const t of toasts) {
      const elapsed = Date.now() - t.pushedAt;
      const remain = Math.max(0, DISMISS_MS - elapsed);
      timers.push(window.setTimeout(() => dismiss(t.id), remain));
    }
    return () => {
      for (const id of timers) window.clearTimeout(id);
    };
  }, [toasts, dismiss]);

  if (toasts.length === 0) return null;
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-50 mb-24 flex w-[340px] flex-col gap-2">
      {toasts.map((t) => (
        <TransportToastRow key={t.id} toast={t} dismiss={dismiss} />
      ))}
    </div>
  );
}

interface TransportToastRowProps {
  toast: ReturnType<typeof useTransport.getState>['recentFailovers'][number];
  dismiss: (id: number) => void;
}

function TransportToastRow({ toast: t, dismiss }: TransportToastRowProps): JSX.Element {
  // Reuse the same display rules the device list uses — see
  // `lib/displayName.ts`. `t.serial` is the registry primary; the
  // hook resolves through the shared `['devices']` cache so we get
  // alias → meta.model → live model → serial without a custom probe.
  const label = useDeviceDisplayName(t.serial) || t.serial;
  const wasOffline = !t.previous;
  const nowOffline = !t.next;
  const wasUsb = t.previous?.kind === 'usb';
  const nowWireless = t.next?.kind === 'wireless';
  let title = '传输方式变更';
  let tone: 'failover' | 'offline' | 'online' = 'failover';
  let Icon = ArrowRight;
  if (wasOffline) {
    title = `${label} 已恢复联机（${describeKind(t.next?.kind)}）`;
    tone = 'online';
    Icon = t.next?.kind === 'usb' ? Cable : Wifi;
  } else if (nowOffline) {
    title = `${label} 离线`;
    tone = 'offline';
    Icon = WifiOff;
  } else if (wasUsb && nowWireless) {
    // Tiered-transport: only `wireless` (Android 11+ wireless
    // debugging) participates in adb routing — a USB → wireless flip
    // means full ADB still works. Tcpip transports never become
    // active, so there's no separate failover path to surface.
    title = `${label} 已自动切换到无线调试`;
    tone = 'failover';
    Icon = Wifi;
  }
  // detail keeps the raw serials — useful for screenshots in bug
  // reports and for distinguishing two phones with identical aliases
  // (rare but happens during the staging fleet rebuild).
  const detail = `${describeKind(t.previous?.kind)} → ${describeKind(t.next?.kind)}${
    t.next?.serial && t.next.serial !== t.serial ? ` · ${t.next.serial}` : ''
  }`;
  return (
    <div
      className={clsx(
        'pointer-events-auto rounded-md border px-3 py-2 text-xs shadow-lg backdrop-blur',
        tone === 'failover' && 'border-sky-500/30 bg-sky-950/85 text-sky-100',
        tone === 'offline' && 'border-rose-500/30 bg-rose-950/85 text-rose-100',
        tone === 'online' && 'border-emerald-500/30 bg-emerald-950/85 text-emerald-100'
      )}
    >
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-[12px] font-semibold">{title}</div>
          <div className="mt-0.5 break-words font-mono text-[11px] opacity-90">{detail}</div>
        </div>
        <button
          type="button"
          onClick={() => dismiss(t.id)}
          className="rounded p-0.5 text-current opacity-70 hover:opacity-100"
          aria-label="dismiss"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
